import {Helmet} from '@dr.pogodin/react-helmet';
import {Card} from 'platform/components';
import {Box} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isString} from 'ramda-adjunct';

import {useListRecordAuditDefinitionQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {recordAuditRoutes} from '@dms/routes';
import {catchUnhandledDataGridActions, DmsDataGrid, Main} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

// TODO T20-120660 Audits POC — vehicle-scoped audits section.
// Reuses the global `record-audit` grid; queryModifier pins the listing to this vehicle by
// (recordId, resource). "New audit" dropdown mirrors AuditOverview but appends recordId so
// AuditCreate prefills the record/resource pairing from the URL.

const RECORD_AUDIT_HISTORY_GRID_CODE = 'record-audit';

interface VehicleAuditsProps {
  vehicleId: string;
}

export function VehicleAudits(props: VehicleAuditsProps) {
  const navigate = useNavigate();

  const definitionsQuery = useListRecordAuditDefinitionQuery({});

  const definitions = definitionsQuery.data ?? [];

  const newAuditMenuItems = definitions.map((definition) => {
    const params = new URLSearchParams({
      recordAuditDefinitionId: definition.id,
      recordId: props.vehicleId,
      recordResourceId: EntityResourceIds.vehicle,
    });
    return {
      label: definition.name,
      leftIcon: 'action/check_circle' as const,
      onClick: () => navigate(`${recordAuditRoutes.create}?${params.toString()}`),
    };
  });

  const queryModifier = (filter: QueryFilterObject) => ({
    ...filter,
    recordId: props.vehicleId,
    resource: EntityResourceIds.vehicle,
  });

  const actionCallback: ActionCallback = ({actionKey, rowId}) => {
    const auditId = isString(rowId) ? rowId : undefined;

    match(actionKey)
      .with('redirectDetail', 'detail', () => {
        if (isString(auditId)) {
          navigate(composePath(recordAuditRoutes.detail, {params: {id: auditId}}));
        }
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <Box flex={1} minWidth={0}>
      <Helmet title={i18n.t('module.checklistAudits.title')} />
      <Main
        isFullHeight
        heading={i18n.t('page.checklistAudits.title')}
        actions={[
          {
            type: 'dropdown-button',
            title: i18n.t('page.checklistAudits.actions.newAudit'),
            variant: 'primary',
            leftIcon: 'content/add',
            menuItems: newAuditMenuItems,
            isDisabled: newAuditMenuItems.length === 0 || definitionsQuery.isLoading,
          },
        ]}
      >
        <Card isFullHeight>
          <DmsDataGrid
            gridCode={RECORD_AUDIT_HISTORY_GRID_CODE}
            presetInstanceId="vehicle-record-audits"
            actionCallback={actionCallback}
            queryModifier={queryModifier}
          />
        </Card>
      </Main>
    </Box>
  );
}
